import { Label, TextInput } from 'flowbite-react';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AccessRight } from 'src/routes/PermissionEnum';
import { useTQuery } from 'src/services/common';
import { instance } from 'src/services/https';
import CustomButton from '../common/custom-buttom';
import DataTablePresentation from './presentation';

interface IConfig {
  url: string;
  key: string[];
}
interface IColumn {
  header: string;
  accessor: string;
}
interface IDataTable {
  payload?: any;
  config: IConfig;
  columns: IColumn[];
  heading?: string;
  csv?: boolean;
  /** When true, shows Export to PDF (same filters as the table, via exportPDF=true). */
  pdf?: boolean;
  RenderAccordion?: (row?: any, index?: number) => React.ReactNode;
  additionalUI?: React.ReactNode;
  filterUI?: (handleChange: any, values: { [key: string]: string | number | Date }) => React.ReactNode
}

const DataTable = ({ payload, config, columns, heading, additionalUI, filterUI, RenderAccordion, csv = false, pdf = false }: IDataTable) => {
  const [values, setValues] = useState<any>({
    page: 1,
    limit: 10,
    search: '',
    ...payload,
  });
  const [searchTerm, setSearchTerm] = useState(values.search);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(values.search);
  const { data, refetch, isFetching }: any = useTQuery({ ...config, params: values });

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleChange = (data) => {
    // Back to page 1: a new filter usually yields fewer pages, and keeping the old
    // page number lands the user on an empty result set.
    setValues((prev) => ({ ...prev, ...data, page: 1 }))
  }
  const downloadExport = async (flag: "exportCSV" | "exportPDF", fallbackName: string) => {
    try {
      // Use the app's configured API base, not a hardcoded prod IP — the old URL
      // meant every CSV export silently hit production and failed in dev.
      // No `credentials: 'include'`: the API sends Access-Control-Allow-Origin: '*',
      // which browsers reject on credentialed requests — that broke every export.
      const base = (instance.defaults.baseURL || '').replace(/\/$/, '');
      const params = new URLSearchParams();
      Object.entries({ ...values, [flag]: "true" }).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        params.set(k, v instanceof Date ? v.toISOString() : String(v));
      });
      const response = await fetch(`${base}/${config.url}?${params}`);

      if (!response.ok) {
        throw new Error("Failed to download");
      }

      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition");

      let fileName = fallbackName;

      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) {
          fileName = match[1].replace(/^['"]|['"]$/g, "");
        }
      }
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  const exportCSV = () => downloadExport("exportCSV", "users.csv");
  const exportPDF = () => downloadExport("exportPDF", "monthly_report.pdf");

  useEffect(() => {
    setValues((prevValues: any) => ({ ...prevValues, search: debouncedSearchTerm, page: 1 }));
  }, [debouncedSearchTerm]);

  useEffect(() => {
    refetch();
  }, [values]);

  return (
    <div className="rounded-xl dark:bg-darkgray   dark:shadow-dark-md bg-white shadow-md dark:bg-darkgray p-6 relative w-full break-words">
      <div className="flex">
        <Label className="flex-1 text-[24px]" htmlFor="heading" value={heading || ''} />

        <div className="flex justify-end items-center mb-2 gap-2">
          {csv && <CustomButton accessRight={AccessRight.ISEDIT} disable={(data?.data.length == 0 || !data?.data) && values?.page == 1} onClick={() => exportCSV()}>Export CSV</CustomButton>

          }
          {pdf && (
            <CustomButton
              accessRight={AccessRight.ISEDIT}
              onClick={() => exportPDF()}
            >
              Export to PDF
            </CustomButton>
          )}
          {additionalUI && additionalUI}
          {filterUI && filterUI(handleChange, values)}
          <TextInput
            id="search"
            type="text"
            placeholder="Search"
            sizing="sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {values?.search &&
            <X size={20} className="bg-red-300 p-1 rounded-full" onClick={() => {
              setValues({ ...values, search: "" })
              setSearchTerm("")
            }} />
          }

        </div>
      </div>
      <DataTablePresentation
        columns={columns}
        onShort={(sort) => setValues({ ...values, ...sort })}
        response={data?.data || {}}
        // pagination={data?.pagination}
        {...(RenderAccordion
          ? { renderAccordion: (row, index) => RenderAccordion(row, index) }
          : {})}
        onPageChange={(page) => setValues({ ...values, page })}
        loading={isFetching}
      />
    </div>
  );
};

export default DataTable;